import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import {
  CheckCircle, Clock, Calendar, BarChart3, Play, Pause, X, Check,
  AlertTriangle, RefreshCw, Plus, FileText, Settings, Eye, Edit,
  ChevronRight, Users, Send, Percent, Target, TrendingUp, Trash2, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = API_BASE;
const TENANT_ID = 1;

const CMSEnhancementsPage = () => {
  const [activeTab, setActiveTab] = useState('approvals');
  const [loading, setLoading] = useState(true);
  
  // Approval workflow state
  const [approvalConfig, setApprovalConfig] = useState(null);
  const [approvalRequests, setApprovalRequests] = useState([]);
  
  // Scheduled publishing state
  const [scheduledPublishes, setScheduledPublishes] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    title: '',
    version_id: null,
    scheduled_for: '',
    timezone: 'UTC',
    auto_unpublish: false,
    unpublish_at: ''
  });
  
  // A/B testing state
  const [abTests, setABTests] = useState([]);
  const [showABTestModal, setShowABTestModal] = useState(false);
  const [newABTest, setNewABTest] = useState({
    name: '',
    description: '',
    hypothesis: '',
    test_type: 'section',
    target_section_id: null,
    traffic_percentage: 50,
    primary_goal: 'clicks',
    variants: [{ name: 'Variant B', traffic_weight: 50, content: {} }]
  });
  
  // Statistical significance state
  const [showSignificanceModal, setShowSignificanceModal] = useState(false);
  const [significanceData, setSignificanceData] = useState(null);
  const [loadingSignificance, setLoadingSignificance] = useState(false);
  
  // Approval request modal
  const [showApprovalRequestModal, setShowApprovalRequestModal] = useState(false);
  const [newApprovalRequest, setNewApprovalRequest] = useState({
    title: '',
    description: '',
    content_type: 'section',
    content_id: null,
    auto_publish_on_approval: false
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ezerpToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [configRes, requestsRes, scheduledRes, testsRes] = await Promise.all([
        fetch(`${API_URL}/api/landing/${TENANT_ID}/approval/config`, { headers }),
        fetch(`${API_URL}/api/landing/${TENANT_ID}/approval/requests`, { headers }),
        fetch(`${API_URL}/api/landing/${TENANT_ID}/scheduled`, { headers }),
        fetch(`${API_URL}/api/landing/${TENANT_ID}/ab-tests`, { headers })
      ]);
      
      if (configRes.ok) setApprovalConfig(await configRes.json());
      if (requestsRes.ok) setApprovalRequests(await requestsRes.json());
      if (scheduledRes.ok) setScheduledPublishes(await scheduledRes.json());
      if (testsRes.ok) setABTests(await testsRes.json());
    } catch (err) {
      console.error('Failed to load CMS data:', err);
      toast.error('Failed to load CMS data');
    } finally {
      setLoading(false);
    }
  };

  const updateApprovalConfig = async (newConfig) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/approval/config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(newConfig)
      });
      if (response.ok) {
        setApprovalConfig(newConfig);
        toast.success('Configuration updated');
      }
    } catch (err) {
      console.error('Failed to update config:', err);
      toast.error('Failed to update configuration');
    }
  };

  const createApprovalRequest = async () => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/approval/requests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newApprovalRequest)
      });
      if (response.ok) {
        toast.success('Approval request created');
        setShowApprovalRequestModal(false);
        setNewApprovalRequest({ title: '', description: '', content_type: 'section', content_id: null, auto_publish_on_approval: false });
        loadData();
      }
    } catch (err) {
      console.error('Failed to create approval request:', err);
      toast.error('Failed to create approval request');
    }
  };

  const approveRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/approval/requests/${requestId}/approve?user_id=1`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Request approved');
        loadData();
      }
    } catch (err) {
      console.error('Failed to approve:', err);
      toast.error('Failed to approve request');
    }
  };

  const rejectRequest = async (requestId, notes) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/approval/requests/${requestId}/reject?user_id=1&notes=${encodeURIComponent(notes || 'Rejected by admin')}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Request rejected');
        loadData();
      }
    } catch (err) {
      console.error('Failed to reject:', err);
      toast.error('Failed to reject request');
    }
  };

  const createScheduledPublish = async () => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/scheduled`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newSchedule,
          scheduled_for: new Date(newSchedule.scheduled_for).toISOString(),
          unpublish_at: newSchedule.unpublish_at ? new Date(newSchedule.unpublish_at).toISOString() : null
        })
      });
      if (response.ok) {
        toast.success('Scheduled publish created');
        setShowScheduleModal(false);
        setNewSchedule({ title: '', version_id: null, scheduled_for: '', timezone: 'UTC', auto_unpublish: false, unpublish_at: '' });
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create schedule');
      }
    } catch (err) {
      console.error('Failed to create schedule:', err);
      toast.error('Failed to create scheduled publish');
    }
  };

  const cancelSchedule = async (scheduleId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/scheduled/${scheduleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Schedule cancelled');
        loadData();
      }
    } catch (err) {
      console.error('Failed to cancel:', err);
      toast.error('Failed to cancel schedule');
    }
  };

  const createABTest = async () => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/ab-tests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newABTest)
      });
      if (response.ok) {
        toast.success('A/B test created');
        setShowABTestModal(false);
        setNewABTest({
          name: '', description: '', hypothesis: '', test_type: 'section',
          target_section_id: null, traffic_percentage: 50, primary_goal: 'clicks',
          variants: [{ name: 'Variant B', traffic_weight: 50, content: {} }]
        });
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create A/B test');
      }
    } catch (err) {
      console.error('Failed to create A/B test:', err);
      toast.error('Failed to create A/B test');
    }
  };

  const startABTest = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/ab-tests/${testId}/start`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('A/B test started');
        loadData();
      }
    } catch (err) {
      console.error('Failed to start test:', err);
      toast.error('Failed to start test');
    }
  };

  const pauseABTest = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/ab-tests/${testId}/pause`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('A/B test paused');
        loadData();
      }
    } catch (err) {
      console.error('Failed to pause test:', err);
      toast.error('Failed to pause test');
    }
  };

  const completeABTest = async (testId, winnerId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/ab-tests/${testId}/complete?winner_variant_id=${winnerId || ''}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('A/B test completed');
        loadData();
      }
    } catch (err) {
      console.error('Failed to complete test:', err);
      toast.error('Failed to complete test');
    }
  };

  const deleteABTest = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/ab-tests/${testId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('A/B test deleted');
        loadData();
      }
    } catch (err) {
      console.error('Failed to delete test:', err);
      toast.error('Failed to delete test');
    }
  };

  const fetchSignificance = async (testId) => {
    setLoadingSignificance(true);
    setShowSignificanceModal(true);
    try {
      const response = await fetch(`${API_URL}/api/landing/${TENANT_ID}/ab-tests/${testId}/significance`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSignificanceData(data);
      } else {
        toast.error('Failed to calculate significance');
        setShowSignificanceModal(false);
      }
    } catch (err) {
      console.error('Failed to fetch significance:', err);
      toast.error('Failed to calculate significance');
      setShowSignificanceModal(false);
    } finally {
      setLoadingSignificance(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    revision_requested: 'bg-purple-100 text-purple-700',
    scheduled: 'bg-blue-100 text-blue-700',
    published: 'bg-green-100 text-green-700',
    cancelled: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-700',
    draft: 'bg-gray-100 text-gray-700',
    running: 'bg-green-100 text-green-700',
    paused: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700'
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">CMS Enhancements</h1>
          <p className="text-muted-foreground">Approval workflows, scheduled publishing &amp; A/B testing</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approvals</p>
                <p className="text-2xl font-bold text-amber-600">
                  {approvalRequests.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Scheduled Publishes</p>
                <p className="text-2xl font-bold text-blue-600">
                  {scheduledPublishes.filter(s => s.status === 'scheduled').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active A/B Tests</p>
                <p className="text-2xl font-bold text-green-600">
                  {abTests.filter(t => t.status === 'running').length}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Impressions</p>
                <p className="text-2xl font-bold text-purple-600">
                  {abTests.reduce((sum, t) => sum + (t.total_impressions || 0), 0).toLocaleString()}
                </p>
              </div>
              <Eye className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="approvals">Approval Workflow</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Publishing</TabsTrigger>
          <TabsTrigger value="abtests">A/B Testing</TabsTrigger>
        </TabsList>

        {/* Approval Workflow Tab */}
        <TabsContent value="approvals" className="space-y-6">
          {/* Workflow Config */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-muted-foreground" />
                  Workflow Configuration
                </CardTitle>
                <Badge className={approvalConfig?.require_approval_for_publish ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                  {approvalConfig?.require_approval_for_publish ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Require Approval for Publish</span>
                    <input
                      type="checkbox"
                      checked={approvalConfig?.require_approval_for_publish}
                      onChange={(e) => updateApprovalConfig({
                        ...approvalConfig,
                        require_approval_for_publish: e.target.checked
                      })}
                      className="w-5 h-5 accent-red-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Auto-approve Minor Changes</span>
                    <input
                      type="checkbox"
                      checked={approvalConfig?.auto_approve_minor_changes}
                      onChange={(e) => updateApprovalConfig({
                        ...approvalConfig,
                        auto_approve_minor_changes: e.target.checked
                      })}
                      className="w-5 h-5 accent-red-500"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Min. Approvals Required</Label>
                    <Input
                      type="number"
                      min={1}
                      value={approvalConfig?.min_approvals_required || 1}
                      onChange={(e) => updateApprovalConfig({
                        ...approvalConfig,
                        min_approvals_required: parseInt(e.target.value)
                      })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Approval Requests */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Approval Requests</CardTitle>
                <Button className="bg-red-500 hover:bg-red-600" onClick={() => setShowApprovalRequestModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Request
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {approvalRequests.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Approval Requests</h3>
                  <p className="text-muted-foreground">Create your first approval request</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvalRequests.map((request) => (
                    <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          request.status === 'pending' ? 'bg-amber-100' : 'bg-muted'
                        }`}>
                          <FileText className={`w-5 h-5 ${
                            request.status === 'pending' ? 'text-amber-600' : 'text-muted-foreground'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{request.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {request.content_type} • {request.request_code}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Submitted {request.requested_at ? new Date(request.requested_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={statusColors[request.status] || 'bg-gray-100'}>
                          {request.status?.replace('_', ' ')}
                        </Badge>
                        {request.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => approveRequest(request.id)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => rejectRequest(request.id, 'Rejected by admin')}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduled Publishing Tab */}
        <TabsContent value="scheduled" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Scheduled Publishes</CardTitle>
                <Button className="bg-red-500 hover:bg-red-600" onClick={() => setShowScheduleModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Publish
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {scheduledPublishes.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Scheduled Publishes</h3>
                  <p className="text-muted-foreground">Schedule content to be published at a future time</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduledPublishes.map((schedule) => (
                    <div key={schedule.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          schedule.status === 'scheduled' ? 'bg-blue-100' : 'bg-muted'
                        }`}>
                          <Clock className={`w-5 h-5 ${
                            schedule.status === 'scheduled' ? 'text-blue-600' : 'text-muted-foreground'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{schedule.title}</p>
                          <p className="text-sm text-muted-foreground">{schedule.schedule_code}</p>
                          <p className="text-xs text-muted-foreground">
                            Scheduled for {schedule.scheduled_for ? new Date(schedule.scheduled_for).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={statusColors[schedule.status] || 'bg-gray-100'}>
                          {schedule.status}
                        </Badge>
                        {schedule.status === 'scheduled' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600"
                            onClick={() => cancelSchedule(schedule.id)}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* A/B Testing Tab */}
        <TabsContent value="abtests" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>A/B Tests</CardTitle>
                <Button className="bg-red-500 hover:bg-red-600" onClick={() => setShowABTestModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Test
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {abTests.length === 0 ? (
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No A/B Tests</h3>
                  <p className="text-muted-foreground">Create your first A/B test to optimize conversions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {abTests.map((test) => (
                    <div key={test.id} className="border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-4 bg-muted/30">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            test.status === 'running' ? 'bg-green-100' : 'bg-muted'
                          }`}>
                            <BarChart3 className={`w-5 h-5 ${
                              test.status === 'running' ? 'text-green-600' : 'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{test.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {test.test_code} • {test.test_type} • {test.variants_count || 2} variants
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">{test.total_impressions?.toLocaleString() || 0} impressions</p>
                            <p className="text-green-600">{test.total_conversions?.toLocaleString() || 0} conversions</p>
                          </div>
                          <Badge className={statusColors[test.status] || 'bg-gray-100'}>
                            {test.status}
                          </Badge>
                          <div className="flex gap-2">
                            {test.status === 'draft' && (
                              <>
                                <Button size="sm" onClick={() => startABTest(test.id)}>
                                  <Play className="w-4 h-4 mr-1" />
                                  Start
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => deleteABTest(test.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {test.status === 'running' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => fetchSignificance(test.id)}>
                                  <BarChart3 className="w-4 h-4 mr-1" />
                                  Stats
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => pauseABTest(test.id)}>
                                  <Pause className="w-4 h-4 mr-1" />
                                  Pause
                                </Button>
                                <Button size="sm" onClick={() => completeABTest(test.id)}>
                                  Complete
                                </Button>
                              </>
                            )}
                            {test.status === 'paused' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => fetchSignificance(test.id)}>
                                  <BarChart3 className="w-4 h-4 mr-1" />
                                  Stats
                                </Button>
                                <Button size="sm" onClick={() => startABTest(test.id)}>
                                  <Play className="w-4 h-4 mr-1" />
                                  Resume
                                </Button>
                              </>
                            )}
                            {test.status === 'completed' && (
                              <Button size="sm" variant="outline" onClick={() => fetchSignificance(test.id)}>
                                <BarChart3 className="w-4 h-4 mr-1" />
                                View Results
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Test Progress Bars */}
                      {test.status !== 'draft' && (
                        <div className="p-4 space-y-3">
                          <div className="flex items-center gap-4">
                            <div className="w-24 text-sm font-medium">Control (A)</div>
                            <div className="flex-1">
                              <div className="w-full h-6 bg-muted rounded-full overflow-hidden relative">
                                <div 
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${Math.min(50, 100)}%` }}
                                />
                                <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                                  50% traffic
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="w-24 text-sm font-medium">Variant B</div>
                            <div className="flex-1">
                              <div className="w-full h-6 bg-muted rounded-full overflow-hidden relative">
                                <div 
                                  className="h-full bg-green-500 rounded-full"
                                  style={{ width: `${Math.min(50, 100)}%` }}
                                />
                                <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                                  50% traffic
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {test.winner_variant_id && (
                        <div className="p-4 bg-green-50 border-t">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Winner Declared!</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approval Request Modal */}
      <Dialog open={showApprovalRequestModal} onOpenChange={setShowApprovalRequestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Approval Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newApprovalRequest.title}
                onChange={(e) => setNewApprovalRequest({...newApprovalRequest, title: e.target.value})}
                placeholder="Content update for hero section"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newApprovalRequest.description}
                onChange={(e) => setNewApprovalRequest({...newApprovalRequest, description: e.target.value})}
                placeholder="Describe the changes..."
              />
            </div>
            <div>
              <Label>Content Type</Label>
              <Select
                value={newApprovalRequest.content_type}
                onValueChange={(v) => setNewApprovalRequest({...newApprovalRequest, content_type: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="section">Section</SelectItem>
                  <SelectItem value="content_block">Content Block</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="branding">Branding</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoPublish"
                checked={newApprovalRequest.auto_publish_on_approval}
                onChange={(e) => setNewApprovalRequest({...newApprovalRequest, auto_publish_on_approval: e.target.checked})}
                className="w-4 h-4 accent-red-500"
              />
              <Label htmlFor="autoPublish">Auto-publish on approval</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalRequestModal(false)}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600" onClick={createApprovalRequest}>Create Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Publish Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Publish</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newSchedule.title}
                onChange={(e) => setNewSchedule({...newSchedule, title: e.target.value})}
                placeholder="Holiday promotion launch"
              />
            </div>
            <div>
              <Label>Scheduled For</Label>
              <Input
                type="datetime-local"
                value={newSchedule.scheduled_for}
                onChange={(e) => setNewSchedule({...newSchedule, scheduled_for: e.target.value})}
              />
            </div>
            <div>
              <Label>Timezone</Label>
              <Select
                value={newSchedule.timezone}
                onValueChange={(v) => setNewSchedule({...newSchedule, timezone: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/Sao_Paulo">São Paulo (BRT)</SelectItem>
                  <SelectItem value="America/New_York">New York (EST)</SelectItem>
                  <SelectItem value="Europe/London">London (GMT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoUnpublish"
                checked={newSchedule.auto_unpublish}
                onChange={(e) => setNewSchedule({...newSchedule, auto_unpublish: e.target.checked})}
                className="w-4 h-4 accent-red-500"
              />
              <Label htmlFor="autoUnpublish">Auto-unpublish after period</Label>
            </div>
            {newSchedule.auto_unpublish && (
              <div>
                <Label>Unpublish At</Label>
                <Input
                  type="datetime-local"
                  value={newSchedule.unpublish_at}
                  onChange={(e) => setNewSchedule({...newSchedule, unpublish_at: e.target.value})}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleModal(false)}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600" onClick={createScheduledPublish}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A/B Test Modal */}
      <Dialog open={showABTestModal} onOpenChange={setShowABTestModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create A/B Test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Test Name</Label>
              <Input
                value={newABTest.name}
                onChange={(e) => setNewABTest({...newABTest, name: e.target.value})}
                placeholder="Hero CTA button test"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newABTest.description}
                onChange={(e) => setNewABTest({...newABTest, description: e.target.value})}
                placeholder="Testing different CTA button colors..."
              />
            </div>
            <div>
              <Label>Hypothesis</Label>
              <Input
                value={newABTest.hypothesis}
                onChange={(e) => setNewABTest({...newABTest, hypothesis: e.target.value})}
                placeholder="Red CTA will increase conversions by 15%"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Test Type</Label>
                <Select
                  value={newABTest.test_type}
                  onValueChange={(v) => setNewABTest({...newABTest, test_type: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="section">Section</SelectItem>
                    <SelectItem value="content_block">Content Block</SelectItem>
                    <SelectItem value="cta">CTA Button</SelectItem>
                    <SelectItem value="pricing">Pricing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Primary Goal</Label>
                <Select
                  value={newABTest.primary_goal}
                  onValueChange={(v) => setNewABTest({...newABTest, primary_goal: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clicks">Clicks</SelectItem>
                    <SelectItem value="conversions">Conversions</SelectItem>
                    <SelectItem value="signups">Signups</SelectItem>
                    <SelectItem value="time_on_page">Time on Page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Traffic Percentage (Control vs Variant)</Label>
              <Input
                type="number"
                min={10}
                max={90}
                value={newABTest.traffic_percentage}
                onChange={(e) => setNewABTest({...newABTest, traffic_percentage: parseInt(e.target.value)})}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Control: {100 - newABTest.traffic_percentage}% | Variant B: {newABTest.traffic_percentage}%
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowABTestModal(false)}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600" onClick={createABTest}>Create Test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Statistical Significance Modal */}
      <Dialog open={showSignificanceModal} onOpenChange={setShowSignificanceModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-red-500" />
              Statistical Significance Analysis
            </DialogTitle>
          </DialogHeader>
          {loadingSignificance ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-red-500" />
            </div>
          ) : significanceData ? (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Test Info */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{significanceData.test_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Confidence Level: {significanceData.confidence_level_target}%
                  </p>
                </div>
                <Badge className={statusColors[significanceData.status] || 'bg-gray-100'}>
                  {significanceData.status}
                </Badge>
              </div>

              {/* Summary Card */}
              <Card className={significanceData.summary?.has_significant_result ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3 mb-3">
                    {significanceData.summary?.has_significant_result ? (
                      <>
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <span className="font-semibold text-green-700">Significant Result!</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                        <span className="font-semibold text-amber-700">Not Yet Significant</span>
                      </>
                    )}
                  </div>
                  <p className="text-sm">{significanceData.summary?.recommendation}</p>
                  {significanceData.summary?.recommended_winner && (
                    <p className="mt-2 font-medium">
                      Recommended Winner: <span className="text-green-700">{significanceData.summary.recommended_winner}</span>
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Control Stats */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    Control (A): {significanceData.control?.variant_name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Impressions</p>
                      <p className="font-semibold text-lg">{significanceData.control?.impressions?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Conversions</p>
                      <p className="font-semibold text-lg">{significanceData.control?.conversions?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Conversion Rate</p>
                      <p className="font-semibold text-lg">{significanceData.control?.conversion_rate}%</p>
                      <p className="text-xs text-muted-foreground">
                        CI: {significanceData.control?.conversion_rate_ci_lower}% - {significanceData.control?.conversion_rate_ci_upper}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Treatment Stats */}
              {significanceData.treatments?.map((treatment, idx) => (
                <Card key={idx} className={treatment.is_significant && treatment.beats_control ? 'border-green-500' : ''}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      {treatment.variant_name || treatment.variant_code}
                      {treatment.is_significant && treatment.beats_control && (
                        <Badge className="bg-green-500 ml-2">Winner</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                      <div>
                        <p className="text-muted-foreground">Impressions</p>
                        <p className="font-semibold text-lg">{treatment.impressions?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Conversions</p>
                        <p className="font-semibold text-lg">{treatment.conversions?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Conversion Rate</p>
                        <p className="font-semibold text-lg">{treatment.conversion_rate}%</p>
                        <p className="text-xs text-muted-foreground">
                          CI: {treatment.conversion_rate_ci_lower}% - {treatment.conversion_rate_ci_upper}%
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-sm border-t pt-4">
                      <div>
                        <p className="text-muted-foreground">Relative Change</p>
                        <p className={`font-semibold ${treatment.beats_control ? 'text-green-600' : 'text-red-600'}`}>
                          {treatment.beats_control ? '+' : ''}{treatment.relative_improvement_percent}%
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Z-Score</p>
                        <p className="font-semibold">{treatment.z_score || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">P-Value</p>
                        <p className={`font-semibold ${treatment.is_significant ? 'text-green-600' : ''}`}>
                          {treatment.p_value?.toFixed(4) || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {treatment.is_significant ? (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg text-sm text-green-700 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Statistically significant at {significanceData.confidence_level_target}% confidence
                      </div>
                    ) : (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Not yet statistically significant
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Sample Size Info */}
              {significanceData.summary?.sample_size_per_variant_needed && (
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Sample Size Analysis</p>
                        <p className="text-sm text-muted-foreground">
                          Need ~{significanceData.summary.sample_size_per_variant_needed?.toLocaleString()} impressions per variant
                        </p>
                      </div>
                      {significanceData.summary?.estimated_days_remaining && (
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Estimated Days Remaining</p>
                          <p className="font-semibold text-lg">{significanceData.summary.estimated_days_remaining}</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 w-full bg-muted rounded-full h-2">
                      <div 
                        className="h-full bg-red-500 rounded-full"
                        style={{ 
                          width: `${Math.min(100, (significanceData.summary?.total_impressions / (significanceData.summary?.sample_size_per_variant_needed * 2)) * 100)}%` 
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Progress: {significanceData.summary?.total_impressions?.toLocaleString()} / {(significanceData.summary?.sample_size_per_variant_needed * 2)?.toLocaleString()} impressions
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No data available
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignificanceModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSEnhancementsPage;
